import { useEffect, useState } from "react";
import { MapPinned, AlertCircle } from "lucide-react";
import { useAdminTranslation } from "@/i18n/hooks";
import { useAuth } from "@/context/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Text } from "@/components/tailwind/text.tsx";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch, SwitchField } from "@/components/tailwind/switch";
import { Label, Description } from "@/components/tailwind/fieldset";
import {
    getPrefillMemberAddressFromOrg,
    updatePrefillMemberAddressFromOrg
} from "@/services/member-prefill-settings-services";
import { getOrganizationDetails } from "@/services/organization-details-services";
import log from '@/lib/logger'

export default function SectionMemberAddressPrefill() {
    const { t } = useAdminTranslation();
    const { token } = useAuth();
    const { toast } = useToast();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [enabled, setEnabled] = useState(false);
    const [orgHasAddress, setOrgHasAddress] = useState(true);

    useEffect(() => {
        loadSettings();
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    async function loadSettings() {
        if (!token) return;

        setIsLoading(true);
        try {
            const [prefillEnabled, details] = await Promise.all([
                getPrefillMemberAddressFromOrg(token),
                getOrganizationDetails(token)
            ]);
            setEnabled(prefillEnabled);
            setOrgHasAddress(!!(details && details.postalCode && details.city));
        } catch (error) {
            log.error("Error loading member address pre-fill settings:", error);
            setEnabled(false);
        } finally {
            setIsLoading(false);
        }
    }

    async function handleToggle(value: boolean) {
        if (!token) return;

        const previous = enabled;
        setEnabled(value);
        setIsSaving(true);
        try {
            await updatePrefillMemberAddressFromOrg(token, value);
            toast({
                title: t('adminSettings.memberAddressPrefill.toast.success.title'),
                description: t('adminSettings.memberAddressPrefill.toast.success.description'),
                variant: "default"
            });
        } catch (error) {
            log.error("Error saving member address pre-fill setting:", error);
            setEnabled(previous);
            toast({
                title: t('adminSettings.memberAddressPrefill.toast.error.title'),
                description: t('adminSettings.memberAddressPrefill.toast.error.description'),
                variant: "destructive"
            });
        } finally {
            setIsSaving(false);
        }
    }

    return (
        <Card className="p-6 mt-6 border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
            <CardHeader>
                <CardTitle className="text-xl font-bold flex items-center gap-2">
                    <MapPinned className="h-5 w-5" />
                    {t('adminSettings.memberAddressPrefill.title')}
                </CardTitle>
            </CardHeader>

            <CardContent>
                {isLoading ? (
                    <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        {t('adminSettings.memberAddressPrefill.loading')}
                    </div>
                ) : (
                    <div className="space-y-4">
                        <SwitchField>
                            <Label>{t('adminSettings.memberAddressPrefill.toggle.label')}</Label>
                            <Description>
                                {t('adminSettings.memberAddressPrefill.toggle.description')}
                            </Description>
                            <Switch
                                checked={enabled}
                                onChange={handleToggle}
                                disabled={isSaving}
                                color="blue"
                                data-testid="member-address-prefill-switch"
                            />
                        </SwitchField>

                        {enabled && !orgHasAddress && (
                            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-950 border-l-4 border-amber-400 dark:border-amber-500 p-4 rounded">
                                <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400 shrink-0 mt-0.5" />
                                <Text className="text-sm text-amber-700 dark:text-amber-300">
                                    {t('adminSettings.memberAddressPrefill.noAddressWarning')}
                                </Text>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
